/**
 * ComposerAttachmentChip
 *
 * One pill in the row above the composer input. Shows the file icon,
 * truncated name, size or upload progress, and an × button. Errored
 * uploads get a red border and dropping them removes the job; the user
 * re-drops the file to retry.
 */

import { AlertCircle, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatFileSize } from '@/utils/format';
import { iconForFile } from '@/utils/file-icon';
import type { AttachmentJob } from './composer-attachments-context';

export interface ComposerAttachmentChipProps {
  job: AttachmentJob;
  onRemove: () => void;
}

export function ComposerAttachmentChip({ job, onRemove }: ComposerAttachmentChipProps) {
  const Icon = iconForFile({ name: job.name, contentType: job.contentType });
  const isError = job.status === 'error';
  const inFlight = job.status === 'pending' || job.status === 'uploading' || job.status === 'completing';

  const meta = isError
    ? (job.error ?? 'Failed')
    : inFlight
      ? job.status === 'uploading'
        ? `${Math.round(job.progress * 100)}%`
        : job.status === 'completing'
          ? 'Finishing…'
          : 'Waiting…'
      : formatFileSize(job.size);

  return (
    <div
      className={cn(
        'group relative flex items-center gap-2 rounded-2xl border border-border bg-background py-1.5 pl-2 pr-2 max-w-[16rem]',
      )}
      title={`${job.path} — ${meta}`}
    >
      {inFlight ? (
        <Loader2 className="h-4 w-4 shrink-0 text-muted-foreground animate-spin" />
      ) : isError ? (
        <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
      ) : (
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <div className="min-w-0 flex flex-col leading-tight">
        <span className="truncate text-xs font-medium text-foreground">{job.name}</span>
        <span className={cn('truncate text-[10px]', isError ? 'text-destructive' : 'text-muted-foreground')}>
          {meta}
        </span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={inFlight ? 'Cancel upload' : 'Remove attachment'}
        className="ml-1 inline-flex items-center justify-center rounded-md h-5 w-5 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
